import type { RotaDay, TimeOffRequest, WeeklyPattern } from "@dentacore/shared-types";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";

import { RowMenu } from "../ui/popovers";
import { daysText, dayStateOf, hoursOf, onLeaveOn, roleLabel, shiftText } from "./roster";
import type { Person } from "./roster";
import styles from "./staff.module.css";
import { WEEKDAY_NAMES } from "./weekdays";
import { isPast } from "./weeks";

/**
 * One person, beside the day (ST12).
 *
 * **Their colour on the head, two figures, then their week.** The figures are
 * the two a manager asks about somebody — hours this week against the
 * contract, and leave left — each with a thin bar that fills. The week is seven
 * lines, each day a pill in their role's colour, a changed day warm and saying
 * what it usually is. **Clicking a day changes it**; the regular week is behind
 * *Edit every week…* at the foot.
 *
 * What the Team tab's card said is all here (ST6 folded it in, S11 stands):
 * today's status, this location's hours against the contract, the other
 * locations and the clinic-wide total, and the leave balance.
 */
export function PersonPanel({
  person,
  pattern,
  day,
  today,
  approved,
  loading,
  busy,
  onClose,
  onChangeDay,
  onEveryWeek,
  onClearWeek,
}: {
  person: Person;
  /** Their regular week, for what a changed day usually is. Null while it loads. */
  pattern: WeeklyPattern | null;
  /** The day open on the card, marked in their week. */
  day: string;
  today: string;
  approved: readonly TimeOffRequest[];
  /** True while the week is being read — nobody is "elsewhere" until it has arrived. */
  loading: boolean;
  busy: boolean;
  onClose: () => void;
  onChangeDay: (day: RotaDay) => void;
  onEveryWeek: () => void;
  onClearWeek: () => void;
}): React.JSX.Element {
  const week = person.week;
  const card = person.card;
  // The server refuses a rota write for anybody who cannot be scheduled here
  // (QA #28, QA #46), so the controls are not offered to be refused.
  /**
   * **The server's rota rule, drawn** (QA third run): your own needs
   * `staff.rota.edit.own`, anyone else's needs `staff.rota.edit.any` as well.
   */
  const { user } = useAuth();
  const can = useCan();
  const mayEdit =
    can("staff.rota.edit.own") && (person.id === user?.id || can("staff.rota.edit.any"));
  const canWrite = mayEdit && person.active && week !== null;

  const status = loading
    ? ""
    : !person.active
      ? "cannot be scheduled"
      : week === null
        ? "not at this location"
        : card === null
          ? ""
          : // The server counts training as off for booking (it is not seeing
            // patients); the day card draws it as in. Said as what it is, so the
            // two never contradict each other on one screen.
            card.todayStatus === "OFF" &&
              week.days.find((row) => row.date === today)?.kind === "TRAINING"
            ? "training today"
            : STATUS_WORDS[card.todayStatus];

  return (
    <aside className={styles.panel} aria-label={person.name} data-testid="person-panel">
      <div className={styles.band} data-role={person.role}>
        <span className={styles.avatar} aria-hidden="true">
          {person.initials}
        </span>
        <div className={styles.bandText}>
          <h2 className={styles.bandName} data-testid="week-person">
            {person.name}
          </h2>
          <span className={styles.bandSub}>
            <span className={styles.bandRole}>{roleLabel(person.role)}</span>
            {status !== "" && (
              <>
                {" · "}
                <span data-testid="team-status">{status}</span>
              </>
            )}
          </span>
        </div>
        {canWrite && week.exceptionCount > 0 && (
          <RowMenu
            label={`More for ${person.name}`}
            items={[
              {
                label: "Clear this week’s changes",
                onSelect: onClearWeek,
                disabled: busy,
                testId: "week-clear",
              },
            ]}
            testId="person-menu"
          />
        )}
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          data-testid="person-close"
        >
          &times;
        </button>
      </div>

      {loading ? (
        <p className={styles.blank}>Reading&hellip;</p>
      ) : !person.active ? (
        <p className={styles.blank}>
          {person.name} has no access to the clinic just now, so they cannot be scheduled. Access is
          restored in System.
        </p>
      ) : week === null ? (
        <p className={styles.blank} data-testid="not-here">
          {person.elsewhere.length > 0
            ? `Works at ${person.elsewhere.join(", ")}. Their hours are set at that location.`
            : "Not at this location. Their hours are set wherever they work."}
        </p>
      ) : (
        <>
          <div className={styles.stats} data-testid="team-card">
            <div className={styles.stat} data-testid="team-hours">
              <b>{hoursOf(week.paidMinutes)}</b>
              <span>
                {week.contractedMinutes === null
                  ? "this week, no contract"
                  : `of ${hoursOf(week.contractedMinutes)} this week`}
              </span>
              <span className={styles.meter} aria-hidden="true">
                <i
                  style={{ width: `${String(fill(week.paidMinutes, week.contractedMinutes))}%` }}
                />
              </span>
            </div>

            <div className={styles.stat} data-testid="team-balance">
              {person.balance === null || person.balance.remainingDays === null ? (
                <>
                  <b>&mdash;</b>
                  <span>
                    {person.balance === null ? "leave not counted yet" : "no leave allowance set"}
                  </span>
                  <span className={styles.meter} aria-hidden="true" />
                </>
              ) : (
                <>
                  <b className={person.balance.remainingDays < 0 ? styles.warmFigure : undefined}>
                    {daysText(person.balance.remainingDays)}
                  </b>
                  <span>of {daysText(person.balance.entitlementDays ?? 0)} days of leave left</span>
                  <span className={styles.meter} aria-hidden="true">
                    <i
                      style={{
                        width: `${String(fill(person.balance.remainingDays, person.balance.entitlementDays))}%`,
                      }}
                    />
                  </span>
                </>
              )}
            </div>

            {/* **Only when it differs** — the second total exists for the person it differs for (S11). */}
            {card !== null && card.elsewhere.length > 0 && (
              <p className={styles.also} data-testid="team-elsewhere">
                Also at {card.elsewhere.join(", ")} · {hoursOf(card.clinicPaidMinutes)} a week
                across the clinic
                {card.contractedMinutes !== null &&
                  card.contractedMinutes > 0 &&
                  `, ${String(Math.round((card.clinicPaidMinutes / card.contractedMinutes) * 100))}% of contract`}
              </p>
            )}
          </div>

          {/*
            **The week is withheld, not faked** (S6). An empty rota and an
            unpublished one are different facts: one means *ask about my
            hours*, the other *wait*.
          */}
          {week.days.length === 0 ? (
            <p className={styles.blank} data-testid="week-unpublished">
              This week&rsquo;s rota has not been published yet.
            </p>
          ) : (
            <ol className={styles.days} data-testid="week-view">
              {week.days.map((row) => {
                const leave = dayStateOf(row, onLeaveOn(person.id, row.date, approved)) === "leave";
                const usual = pattern?.days.find((entry) => entry.dayOfWeek === row.dayOfWeek);
                const weekday = WEEKDAY_NAMES[row.dayOfWeek] ?? "";
                const extra = [
                  row.fromException && usual !== undefined ? `usually ${shiftText(usual)}` : "",
                ].filter((text) => text !== "");

                return (
                  /*
                    **A finished day looks finished** (Anthony's QA, item 5). It
                    stays changeable — a sick day nobody logged is otherwise
                    permanently wrong — and the dialog asks before it saves.
                  */
                  <li
                    key={row.date}
                    data-past={isPast(row.date) ? "" : undefined}
                    data-testid="dated-day"
                  >
                    <button
                      type="button"
                      className={row.date === day ? `${styles.dRow} ${styles.dRowOn}` : styles.dRow}
                      data-card=""
                      disabled={!canWrite}
                      onClick={() => onChangeDay(row)}
                      aria-label={`Change ${weekday} ${String(Number(row.date.slice(8, 10)))}`}
                      data-testid="dated-day-open"
                    >
                      <span className={styles.dWhen}>
                        <b>{weekday.slice(0, 3)}</b> {Number(row.date.slice(8, 10))}
                      </span>
                      <span className={styles.dValue}>
                        {leave ? (
                          <span className={styles.pillLeave} data-testid="dated-day-leave">
                            On leave
                          </span>
                        ) : row.kind === "OFF" ? (
                          <span
                            className={
                              row.fromException
                                ? `${styles.offWord} ${styles.offChanged}`
                                : styles.offWord
                            }
                          >
                            {row.fromException && (
                              <span className={styles.changedDot} aria-hidden="true" />
                            )}
                            Off
                          </span>
                        ) : (
                          <span
                            className={[
                              styles.pill,
                              row.kind === "TRAINING" ? styles.pillTraining : "",
                              row.fromException ? styles.pillChanged : "",
                            ].join(" ")}
                            data-role={person.role}
                          >
                            {row.fromException && (
                              <span className={styles.changedDot} aria-hidden="true" />
                            )}
                            {shiftText(row)}
                          </span>
                        )}
                        {row.fromException && (
                          <span className={styles.changedWord} data-testid="dated-day-changed">
                            changed
                          </span>
                        )}
                        {(extra.length > 0 || (row.note !== null && row.note !== "")) && (
                          <small className={styles.dExtra}>
                            {extra.join(" · ")}
                            {extra.length > 0 && row.note !== null && row.note !== "" && " · "}
                            {row.note !== null && row.note !== "" && (
                              <span data-testid="dated-day-note">{row.note}</span>
                            )}
                          </small>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          <div className={styles.panelFoot}>
            {mayEdit && (
              <button
                type="button"
                className={styles.ghost}
                disabled={!canWrite}
                onClick={onEveryWeek}
                data-testid="tab-pattern"
              >
                Edit every week&hellip;
              </button>
            )}
            {mayEdit && week.days.length > 0 && (
              <span className={styles.footNote}>Click a day to change it</span>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

/** How full a bar is, 0 to 100, with nothing to fill against drawing empty. */
function fill(value: number, of: number | null): number {
  if (of === null || of <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / of) * 100)));
}

const STATUS_WORDS: Record<string, string> = {
  IN: "in today",
  OFF: "off today",
  ON_LEAVE: "on leave today",
};
